import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CdkDragDrop,
  moveItemInArray,
  DragDropModule,
} from '@angular/cdk/drag-drop';

// Angular Material
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeleteDialogComponent } from '../delete/delete-dialog.component';
import { ResourceAttributeDialogComponent } from './resourceattribute-dialog.component';

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  external?: boolean;
  resourceTypeId: string;
  order: number | null;
  [key: string]: any;
}

@Pipe({
  name: 'sort',
  standalone: true,
})
export class SortPipe implements PipeTransform {
  transform(array: any[], field: string): any[] {
    if (!array || !field) {
      return array;
    }
    return [...array].sort((a, b) => (a[field] || 0) - (b[field] || 0));
  }
}

@Component({
  selector: 'app-resourceattributes',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    DragDropModule,
    SortPipe,
  ],
  templateUrl: './resourceattributes.component.html',
  styleUrls: ['./resourceattributes.component.scss'],
})
export class ResourceAttributesComponent implements OnInit {
  resourceAttributes: ResourceAttribute[] = [];
  loading = false;
  error: string | null = null;
  resourceTypeName: string = '';

  displayedColumns: string[] = [
    'drag',
    'id',
    'name',
    'type',
    'actions',
  ];

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  private resourceTypeId: string = '';

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Try to get resource type name from router state
    const navigation = this.router.getCurrentNavigation();
    if (navigation && navigation.extras.state) {
      this.resourceTypeName =
        (navigation.extras.state as any).resourceTypeName || '';
    }
  }

  ngOnInit() {
    // Get params from route
    this.route.params.subscribe((params) => {
      this.organisationId = params['organisationId'];
      this.resourceTypeId = params['resourceTypeId'];

      if (!this.organisationId || !this.resourceTypeId) {
        this.error = 'Missing organisation or resource type ID.';
        return;
      }

      this.fetchResourceAttributes();
    });
  }

  // Helper method to ensure toPromise is available
  private toPromise<T>(observable: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      observable.subscribe({
        next: (value: T) => resolve(value),
        error: (error: any) => reject(error),
      });
    });
  }

  fetchResourceAttributes() {
    this.loading = true;
    this.http
      .get<ResourceAttribute[]>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/attributes`
      )
      .subscribe({
        next: (data) => {
          this.resourceAttributes = data;
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load resource attributes', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  openAddDialog() {
    // Calculate the next order value (last + 10)
    const sortedAttributes = [...this.resourceAttributes].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    const nextOrder =
      sortedAttributes.length > 0
        ? (sortedAttributes[sortedAttributes.length - 1].order || 0) + 10
        : 10;

    const dialogRef = this.dialog.open(ResourceAttributeDialogComponent, {
      width: '600px',
      data: {
        name: '',
        slug: '',
        type: 'text',
        order: nextOrder, // Still set order automatically in the background
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.createResourceAttribute(result);
      }
    });
  }

  openEditDialog(attribute: ResourceAttribute) {
    const dialogRef = this.dialog.open(ResourceAttributeDialogComponent, {
      width: '600px',
      data: { ...attribute },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.updateResourceAttribute(attribute, result);
      }
    });
  }

  createResourceAttribute(newAttribute: Partial<ResourceAttribute>) {
    this.http
      .post<ResourceAttribute>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/attributes`,
        newAttribute
      )
      .subscribe({
        next: (createdAttribute) => {
          this.snackBar.open('Resource attribute created', 'Close', {
            duration: 2000,
          });

          // After creating, ensure all attributes have proper sequential ordering
          const updatedAttributes = [
            ...this.resourceAttributes,
            createdAttribute,
          ].sort((a, b) => (a.order || 0) - (b.order || 0));

          // Only reorder if we have multiple attributes
          if (updatedAttributes.length > 1) {
            this.updateAllAttributeOrders(updatedAttributes);
          } else {
            this.fetchResourceAttributes();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to create resource attribute', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateResourceAttribute(
    attribute: ResourceAttribute,
    updatedData: Partial<ResourceAttribute>
  ) {
    this.http
      .put<ResourceAttribute>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/attributes/${attribute.id}`,
        updatedData
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Resource attribute updated', 'Close', {
            duration: 2000,
          });
          this.fetchResourceAttributes();
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to update resource attribute', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  onDrop(event: CdkDragDrop<ResourceAttribute[]>) {
    if (event.previousIndex === event.currentIndex) {
      return; // No change in position
    }

    // Create a copy of the data array sorted by current order
    const sortedAttributes = [...this.resourceAttributes].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    // Update the array order
    moveItemInArray(sortedAttributes, event.previousIndex, event.currentIndex);

    // Update all attributes with sequential order values
    this.updateAllAttributeOrders(sortedAttributes);
  }

  updateAllAttributeOrders(sortedAttributes: ResourceAttribute[]) {
    // Assign sequential order values (10, 20, 30...) to maintain spacing for future inserts
    // Skip external attributes as they cannot be modified
    const updatePromises = sortedAttributes.map((attr, index) => {
      const newOrder = (index + 1) * 10;
      if (newOrder !== attr.order && !attr.external) {
        return this.http
          .put<ResourceAttribute>(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/attributes/${attr.id}`,
            { ...attr, order: newOrder }
          )
          .toPromise();
      }
      return Promise.resolve();
    });

    // Wait for all updates to complete, then refresh the list
    Promise.all(updatePromises)
      .then(() => {
        this.snackBar.open('Attribute order updated', 'Close', {
          duration: 2000,
        });
        this.fetchResourceAttributes();
      })
      .catch((err) => {
        this.error = err.error?.error || err.message;
        this.snackBar.open('Failed to update attribute order', 'Close', {
          duration: 3000,
        });
      });
  }

  deleteResourceAttribute(attribute: ResourceAttribute) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Resource Attribute',
        message: `Are you sure you want to delete attribute "${attribute.name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.resourceTypeId}/attributes/${attribute.id}`
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Resource attribute deleted', 'Close', {
                duration: 2000,
              });
              this.fetchResourceAttributes();
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open(
                'Failed to delete resource attribute',
                'Close',
                {
                  duration: 3000,
                }
              );
            },
          });
      }
    });
  }

  goBack() {
    this.router.navigate(['/resourcetypes']);
  }
}
